import { z } from 'zod';
import { Cents, IsoDateTime, Uuid } from './common.js';

/**
 * Support (lots BB…BG, contrat GELÉ le 20/10/2026, CDC « Support, coach de journée, mails fournisseurs ») :
 * conversations Crisp et mails des clients, analyse de Claude (lot BD), mémoire (lot BE), mails des fournisseurs
 * (lot BG). Ce fichier est le contrat des routes `/api/support/*` et `/api/admin/support/settings`.
 */

// ------------------------------------------------------------------ Énumérations

export const SupportChannel = z.enum(['crisp', 'gmail']);
export type SupportChannel = z.infer<typeof SupportChannel>;

/**
 * Statut d'une conversation : `WAITING_US` (le client attend), `WAITING_CLIENT` (on attend le client), `DONE`
 * (traitée), `IGNORED` (écartée : spam, « merci »…). Une conversation DONE ou IGNORED redevient WAITING_US quand un
 * nouveau message du client arrive.
 */
export const SupportStatus = z.enum(['WAITING_US', 'WAITING_CLIENT', 'DONE', 'IGNORED']);
export type SupportStatus = z.infer<typeof SupportStatus>;

export const SupportSpeaker = z.enum(['client', 'us']);
export type SupportSpeaker = z.infer<typeof SupportSpeaker>;

/** Catégorie posée par l'analyse (lot BD) ; `partner` dès la synchro pour un mail d'un apporteur. */
export const SupportCategory = z.enum([
  'site_edit',
  'how_to',
  'known_issue',
  'product_change',
  'call',
  'quote',
  'off_site',
  'billing',
  'partner',
  'info',
  'no_reply_needed',
  'spam',
]);
export type SupportCategory = z.infer<typeof SupportCategory>;

/** Libellés des catégories (puces de la maquette). */
export const SUPPORT_CATEGORY_LABELS: Record<SupportCategory, string> = {
  site_edit: 'Modif du site',
  how_to: 'Comment faire',
  known_issue: 'Problème connu',
  product_change: 'Évolution du produit',
  call: 'Appel',
  quote: 'Devis',
  off_site: 'Hors site',
  billing: 'Facturation',
  partner: 'Apporteur',
  info: 'Pour info',
  no_reply_needed: 'Pas de réponse',
  spam: 'Spam',
};

export const SupportDecision = z.enum([
  'reply',
  'site_edit',
  'call',
  'quote',
  'product_change',
  'known_issue',
  'no_reply_needed',
]);
export type SupportDecision = z.infer<typeof SupportDecision>;

/** Sens d'un message : `in` (le client), `out` (l'équipe), `note` (note privée Crisp, visible de l'équipe seulement). */
export const SupportMessageDirection = z.enum(['in', 'out', 'note']);
export type SupportMessageDirection = z.infer<typeof SupportMessageDirection>;

// ------------------------------------------------------------------ Mémoire (lot BE)

export const SupportMemoryKind = z.enum(['known_issue', 'answer', 'client_fact', 'supplier_decision']);
export type SupportMemoryKind = z.infer<typeof SupportMemoryKind>;

export const SupportMemoryStatus = z.enum(['PROPOSED', 'ACTIVE', 'ARCHIVED']);
export type SupportMemoryStatus = z.infer<typeof SupportMemoryStatus>;

export const KnownIssueData = z.object({
  symptoms: z.string(),
  cause: z.string(),
  fixStatus: z.enum(['OPEN', 'FIXED']),
  fixedIn: z.string().optional(),
  clientAction: z.enum(['none', 'rebuild']),
  replyTemplate: z.string(),
});
export type KnownIssueData = z.infer<typeof KnownIssueData>;

export const ClientFactData = z.object({ fact: z.string() });
export type ClientFactData = z.infer<typeof ClientFactData>;

export const AnswerData = z.object({ question: z.string(), answer: z.string() });
export type AnswerData = z.infer<typeof AnswerData>;

export const SupplierVerdict = z.enum(['important', 'ignored', 'pending']);
export type SupplierVerdict = z.infer<typeof SupplierVerdict>;

export const SupplierDecisionData = z.object({
  match: z.object({
    senderDomain: z.string().optional(),
    subjectContains: z.string().optional(),
    domainName: z.string().optional(),
  }),
  verdict: z.enum(['ignore', 'important']),
  note: z.string(),
});
export type SupplierDecisionData = z.infer<typeof SupplierDecisionData>;

/** `support_memory.data` selon `kind`. */
export const SUPPORT_MEMORY_DATA = {
  known_issue: KnownIssueData,
  answer: AnswerData,
  client_fact: ClientFactData,
  supplier_decision: SupplierDecisionData,
} as const satisfies Record<SupportMemoryKind, z.ZodType>;

/** Proposition d'entrée de mémoire (analyse, « Retiens ça »). */
export const SupportMemoryProposal = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('known_issue'), title: z.string(), body: z.string(), data: KnownIssueData }),
  z.object({ kind: z.literal('answer'), title: z.string(), body: z.string(), data: AnswerData }),
  z.object({ kind: z.literal('client_fact'), title: z.string(), body: z.string(), data: ClientFactData }),
  z.object({
    kind: z.literal('supplier_decision'),
    title: z.string(),
    body: z.string(),
    data: SupplierDecisionData,
  }),
]);
export type SupportMemoryProposal = z.infer<typeof SupportMemoryProposal>;

// ------------------------------------------------------------------ Analyse (remplie par le lot BD)

/** Résumé d'une analyse : 140 caractères au plus. */
export const SUPPORT_SUMMARY_MAX = 140;

/**
 * `support_conversations.analysis` (contrat) : ce que Claude a compris, la réponse prête (ton de l'équipe, tu/vous
 * selon le tutoiement), les questions à poser, le prompt de modification, les sources consultées.
 */
export const SupportAnalysis = z.object({
  summary: z.string().max(SUPPORT_SUMMARY_MAX),
  category: SupportCategory,
  effortMinutes: z.int().min(0),
  decision: SupportDecision,
  understood: z.object({
    request: z.string(),
    onSite: z.string().nullable(),
    missing: z.array(z.string()),
  }),
  reply: z.string().nullable(),
  questions: z.array(z.string()),
  prompt: z
    .object({
      target: z.enum(['mcp_editor', 'tech_pm']),
      repo: z.string().nullable(),
      text: z.string(),
    })
    .nullable(),
  callBrief: z.string().nullable(),
  quoteBrief: z.string().nullable(),
  knownIssueId: Uuid.nullable(),
  tutoiement: z.boolean().nullable(),
  memoryProposal: SupportMemoryProposal.nullable(),
  sources: z.array(z.string()),
});
export type SupportAnalysis = z.infer<typeof SupportAnalysis>;

// ------------------------------------------------------------------ Conversations

/**
 * Pièce jointe d'un message. `url` : adresse du fichier chez Crisp, adresse publique d'un fichier joint à une réponse
 * (lot BU), ou route authentifiée du Cockpit pour une pièce jointe Gmail ; null : seul le nom est connu.
 */
export const SupportAttachment = z.object({
  name: z.string(),
  url: z.string().nullable(),
  mime: z.string().nullable(),
  /** Taille en octets, quand elle est connue. */
  size: z.number().int().nonnegative().nullable().optional(),
});
export type SupportAttachment = z.infer<typeof SupportAttachment>;

/** Lot BU : fichier joint à une réponse, déposé dans le Cockpit ; rien ne part avant la validation du plan. */
export const SupportUpload = z.object({
  id: Uuid,
  name: z.string(),
  mime: z.string(),
  size: z.number().int().nonnegative(),
  /** Image matricielle dont le type est vérifié sur les octets. */
  image: z.boolean(),
});
export type SupportUpload = z.infer<typeof SupportUpload>;

/** Une conversation dans la liste (`GET /api/support/conversations`). */
export const SupportConversationItem = z.object({
  id: Uuid,
  channel: SupportChannel,
  /** Boîte Gmail lue (adresse), null pour Crisp. */
  mailbox: z.string().nullable(),
  clientId: Uuid.nullable(),
  /** Nom du client relié, null si la conversation n'est reliée à aucun client. */
  clientName: z.string().nullable(),
  evolusiteSiteId: z.string().nullable(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  subject: z.string().nullable(),
  status: SupportStatus,
  lastSpeaker: SupportSpeaker,
  lastMessageAt: IsoDateTime,
  lastInboundAt: IsoDateTime.nullable(),
  /** Premier message du client resté sans réponse (null si c'est nous qui avons parlé en dernier). */
  waitingSince: IsoDateTime.nullable(),
  category: SupportCategory.nullable(),
  effortMinutes: z.int().nullable(),
  assigneeId: Uuid.nullable(),
  actionId: Uuid.nullable(),
  /** Résumé de l'analyse (lot BD), null tant qu'elle n'est pas faite. */
  summary: z.string().nullable(),
  /** Une réponse préparée existe (analyse faite avec `reply`). */
  replyReady: z.boolean(),
  /** Lot BD : décision de l'analyse (`quote` : « Carte pour … », `call` : appel), null tant qu'elle n'est pas faite. */
  decision: SupportDecision.nullable().optional(),
  /** Extrait du dernier message du client. */
  preview: z.string().nullable(),
  messageCount: z.int().min(0),
  updatedAt: IsoDateTime,
  /** Lot BO : mail reçu pendant la reprise de l'arriéré (plus de 48 h) : ni ping, ni copie automatique dans Crisp. */
  backlog: z.boolean().optional(),
  /** Lot BO : copie du fil Gmail dans Crisp : `none`, `copied` (la conversation Crisp porte le suivi), `error`. */
  crispCopy: z.enum(['none', 'copied', 'error']).optional(),
  /** Lot BV : mail d'un expéditeur inconnu, dans « Mails à trier ». */
  triage: z.boolean().optional(),
});
export type SupportConversationItem = z.infer<typeof SupportConversationItem>;

export const SupportConversationList = z.array(SupportConversationItem);
export type SupportConversationList = z.infer<typeof SupportConversationList>;

/**
 * Filtre de statut de la liste : `active` (défaut) = le client attend, en attente du client, et les conversations
 * écartées des 30 derniers jours (ligne « messages écartés ») ; `all` = tout ; sinon un statut précis.
 */
export const SupportStatusFilter = z.enum(['active', 'all', ...SupportStatus.options]);
export type SupportStatusFilter = z.infer<typeof SupportStatusFilter>;

/** `GET /api/support/conversations?status=&assignee=&channel=` */
export const SupportConversationsQuery = z.object({
  status: SupportStatusFilter.default('active'),
  /** `all` (défaut), `me`, ou l'identifiant d'un membre. */
  assignee: z.union([z.enum(['all', 'me']), Uuid]).default('all'),
  channel: z.enum(['all', 'crisp', 'gmail']).default('all'),
});
export type SupportConversationsQuery = z.input<typeof SupportConversationsQuery>;

export const SupportMessage = z.object({
  id: Uuid,
  direction: SupportMessageDirection,
  authorName: z.string().nullable(),
  authorEmail: z.string().nullable(),
  /** Texte brut, sans les citations ni les signatures répétées. */
  body: z.string(),
  sentAt: IsoDateTime,
  attachments: z.array(SupportAttachment),
});
export type SupportMessage = z.infer<typeof SupportMessage>;

/** Client relié à une conversation (en-tête du panneau). */
export const SupportClient = z.object({
  id: Uuid,
  name: z.string(),
  evolusiteSiteId: z.string().nullable(),
  siteUrl: z.string().nullable(),
  /** Le site est en ligne (back Evolusite), null si le client n'a pas de site connu. */
  siteOnline: z.boolean().nullable(),
  subscription: z
    .object({ planName: z.string().nullable(), monthlyAmountCents: Cents, status: z.string() })
    .nullable(),
});
export type SupportClient = z.infer<typeof SupportClient>;

/** Lot BD : carte de « À faire » liée à une conversation (carte `support`, carte devis). */
export const SupportCard = z.object({
  id: Uuid,
  title: z.string(),
  dueDate: z.string(),
  status: z.enum(['OPEN', 'WAITING_REPLY', 'REPLY_TO_HANDLE', 'DONE', 'DISMISSED']),
  assigneeId: Uuid.nullable(),
});
export type SupportCard = z.infer<typeof SupportCard>;

/**
 * Lot BD : état de l'analyse d'une conversation : `none` (rien à analyser), `pending` (de nouveaux messages du client
 * attendent l'analyse : « Analyse en cours… »), `ready`, `error` (« Analyse impossible : … », bouton « Relancer »).
 */
export const SupportAnalysisState = z.enum(['none', 'pending', 'ready', 'error']);
export type SupportAnalysisState = z.infer<typeof SupportAnalysisState>;

/** Lot BE : problème connu rattaché à une conversation (lien « Problème connu : … » du panneau d'analyse). */
export const SupportKnownIssueRef = z.object({
  id: Uuid,
  title: z.string(),
  fixStatus: z.enum(['OPEN', 'FIXED']),
});
export type SupportKnownIssueRef = z.infer<typeof SupportKnownIssueRef>;

/**
 * Lot BO : le fil Gmail d'une demande de support et sa copie dans Crisp. Sur le fil Gmail : `conversationId` est la
 * conversation Crisp qui porte le suivi. Sur la conversation Crisp : `gmailConversationId` est le fil d'origine.
 * `archive` : `waiting` (pas encore résolue dans Crisp), `archived`, `scope_missing` (« Reconnecte ton compte
 * Google »), `uncopied_message` (un message du client n'est pas dans Crisp : le fil reste), `error`.
 */
export const SupportCrispCopy = z.object({
  state: z.enum(['none', 'copied', 'error']),
  error: z.string().nullable(),
  canCopy: z.boolean(),
  conversationId: Uuid.nullable(),
  gmailConversationId: Uuid.nullable(),
  gmailUrl: z.string().nullable(),
  archive: z.enum(['none', 'waiting', 'archived', 'scope_missing', 'uncopied_message', 'error']),
  archivedAt: IsoDateTime.nullable(),
});
export type SupportCrispCopy = z.infer<typeof SupportCrispCopy>;

/** `POST /api/support/conversations/copy-to-crisp` (bouton groupé) : conversations de la reprise en attente de réponse. */
export const SupportBulkCopyResult = z.object({
  copied: z.int().min(0),
  failed: z.int().min(0),
  /** Restent à copier après ce passage (le bouton relance tant qu'il en reste et que ça avance). */
  remaining: z.int().min(0),
  /** Première erreur lisible, null si tout est passé. */
  error: z.string().nullable(),
});
export type SupportBulkCopyResult = z.infer<typeof SupportBulkCopyResult>;

/** `GET /api/support/conversations/:id` */
export const SupportConversationDetail = z.object({
  conversation: SupportConversationItem,
  messages: z.array(SupportMessage),
  client: SupportClient.nullable(),
  /** Analyse de Claude (lot BD), null tant qu'elle n'est pas faite. */
  analysis: SupportAnalysis.nullable(),
  /** Résumé du site lu dans le back (lots BC/BD), null pour l'instant. */
  site: z.record(z.string(), z.unknown()).nullable(),
  /**
   * Archivage Gmail du fil (boîte lue) : `ok`, `scope_missing` (« Reconnecte ton compte Google pour l'archivage » :
   * l'archivage sera sauté, rien d'autre ne casse), `none` (conversation Crisp).
   */
  archive: z.enum(['ok', 'scope_missing', 'none']),
  links: z.object({
    /** Conversation dans Crisp (« Ouvrir dans Crisp ↗ »), fil dans Gmail, ou null. */
    external: z.string().nullable(),
    externalLabel: z.string(),
  }),
  /** Lot BD : état de l'analyse (ajout). */
  analysisState: SupportAnalysisState.optional(),
  /** Lot BD : « Analyse impossible : … » (ajout), null sinon. */
  analysisError: z.string().nullable().optional(),
  /** Lot BD : carte `support` de « À faire » (« Reporter » change son échéance), null s'il n'y en a pas. */
  card: SupportCard.nullable().optional(),
  /** Lot BD : carte devis (décision `quote`), null s'il n'y en a pas. */
  quoteCard: SupportCard.nullable().optional(),
  /** Lot BE : problème connu reconnu par l'analyse (ajout), null sinon. */
  knownIssue: SupportKnownIssueRef.nullable().optional(),
  /** Lot BO : copie du fil Gmail dans Crisp, archivage du fil une fois résolu (ajout). */
  crisp: SupportCrispCopy.optional(),
});
export type SupportConversationDetail = z.infer<typeof SupportConversationDetail>;

/**
 * `PATCH /api/support/conversations/:id` (geste interne, sans plan) : attribuer, écarter (`IGNORED`) ou marquer
 * traitée (`DONE`), relier à un client (null : délier).
 */
export const UpdateSupportConversationBody = z
  .object({
    assigneeId: Uuid.nullable().optional(),
    status: z.enum(['IGNORED', 'DONE']).optional(),
    clientId: Uuid.nullable().optional(),
    /** Lot BD, « C'est un appel » : la conversation se traite par téléphone (catégorie `call`, carte « Appeler … »). */
    call: z.literal(true).optional(),
  })
  .refine(
    (b) =>
      b.assigneeId !== undefined || b.status !== undefined || b.clientId !== undefined || b.call === true,
    { message: 'Aucune modification demandée' },
  );
export type UpdateSupportConversationBody = z.input<typeof UpdateSupportConversationBody>;

/** Événement SSE `support.changed` : une synchro ou un geste a changé ces conversations. */
export const SupportChangedEvent = z.object({
  conversationIds: z.array(Uuid),
  reason: z.enum(['sync', 'update']),
});
export type SupportChangedEvent = z.infer<typeof SupportChangedEvent>;

// ------------------------------------------------------------------ Réglages > Support

/** Domaines fournisseurs par défaut (contrat) : leurs mails vont dans `supplier_mails`, jamais en conversation. */
export const DEFAULT_SUPPLIER_DOMAINS = [
  'ovh.com',
  'ovhcloud.com',
  'clever-cloud.com',
  'google.com',
  'axonaut.com',
  'stripe.com',
  'github.com',
  'anthropic.com',
] as const;

const DOMAIN = /^(?=.{3,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/**
 * `settings.support` : boîtes Gmail lues pour le support (adresses : celle d'un membre qui a connecté son compte
 * Google, ou une boîte partagée prête) et domaines des fournisseurs. Par défaut : la boîte de l'administrateur.
 */
export const SupportSettings = z.object({
  mailboxes: z
    .array(
      z
        .string()
        .trim()
        .toLowerCase()
        .pipe(z.email({ message: 'Adresse e-mail invalide' })),
    )
    .max(10)
    .transform((list) => [...new Set(list)]),
  supplierDomains: z
    .array(
      z
        .string()
        .trim()
        .toLowerCase()
        .transform((d) => d.replace(/^@/, '').replace(/^www\./, ''))
        .pipe(z.string().regex(DOMAIN, 'Nom de domaine attendu (ex. ovh.com)')),
    )
    .max(100)
    .transform((list) => [...new Set(list)]),
  /** Lot BO : « Copier les demandes de support par mail dans Crisp » (activé par défaut). */
  copyMailsToCrisp: z.boolean().default(false),
});
export type SupportSettings = z.infer<typeof SupportSettings>;
export type SupportSettingsInput = z.input<typeof SupportSettings>;

/** État d'une boîte proposée dans Réglages > Support. */
export const SupportMailboxOption = z.object({
  address: z.string(),
  kind: z.enum(['member', 'shared']),
  /** « Maxime » ou « Boîte partagée ». */
  label: z.string(),
  /**
   * `ok` : lisible ; `not_connected` : le membre n'a pas connecté son compte Google ; `not_ready` : boîte
   * partagée pas encore testée ; `unknown` : ni un membre ni une boîte partagée.
   */
  read: z.enum(['ok', 'not_connected', 'not_ready', 'unknown']),
  /** L'archivage (`gmail.modify`) est autorisé ; false : « Reconnecte ton compte Google pour l'archivage ». */
  archive: z.boolean(),
  selected: z.boolean(),
});
export type SupportMailboxOption = z.infer<typeof SupportMailboxOption>;

/**
 * Lot BS : où en est la lecture d'une boîte. `upTo` : tout est lu jusque-là ; `stoppedBy` : passe arrêtée avant la fin
 * (limite de Gmail, ou budget de lectures), elle reprend toute seule ; `error` : boîte illisible.
 */
export const SupportMailSyncBox = z.object({
  address: z.string(),
  read: SupportMailboxOption.shape.read,
  lastRunAt: z.iso.datetime().nullable(),
  upTo: z.iso.datetime().nullable(),
  caughtUp: z.boolean(),
  stoppedBy: z.enum(['quota', 'budget']).nullable(),
  error: z.string().nullable(),
});
export type SupportMailSyncBox = z.infer<typeof SupportMailSyncBox>;

/** `GET /api/support/mail-sync` */
export const SupportMailSync = z.object({ boxes: z.array(SupportMailSyncBox) });
export type SupportMailSync = z.infer<typeof SupportMailSync>;

/** `GET/PUT /api/admin/support/settings` */
export const SupportSettingsView = z.object({
  settings: SupportSettings,
  isDefault: z.boolean(),
  mailboxes: z.array(SupportMailboxOption),
  defaultSupplierDomains: z.array(z.string()),
  /** Lot BO : notifications Crisp reçues par mail : archivées, ou gardées dans la boîte faute de preuve. */
  crispNotifications: z.object({ archived: z.int().min(0), kept: z.int().min(0) }).optional(),
  /** Lot BS : où en est la lecture de chaque boîte cochée. */
  mailSync: z.array(SupportMailSyncBox).optional(),
});
export type SupportSettingsView = z.infer<typeof SupportSettingsView>;

export const UpdateSupportSettingsBody = SupportSettings;
export type UpdateSupportSettingsBody = z.input<typeof UpdateSupportSettingsBody>;
